import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfigContext } from "../../contexts/app-config.context";
import { getBusinessEntitiesDetail } from '../../api/users.api';
import { ACCESS_TKN_KEY, BUSINESS_ENTITY, GRAPH_TOKEN, ROLES, StorageService, USER_DATA } from '../../services/storage_service';
import "./styles.scss";
import { login } from "../../api/login.api";
import { Roles } from "../../constants/roles.constants";
import { acquireAccessToken, acquireGraphToken, getActiveAccount, msalInstance } from "../../auth/msal";
import { useAnalyticsEvent } from "../../hooks/useAnalyticsEvent";
import { useDarkMode } from "../../hooks/use-darkmode";
import { LoadingSpinner } from "../../components/loading-spinner";
import firebase from "../../utils/firebase.util";

interface Props {
  setShowMapPage: (arg: boolean) => void;
}

interface UserRoleType {
  role: string;
  roleId: string;
}

function AuthenticationPage(props: Readonly<Props>) {
  const logAnalyticsEvent = useAnalyticsEvent();
  const {darkMode} = useDarkMode();
  const { setShowMapPage } = props;
  const { t } = useTranslation();
  const appConfigStore = useContext(AppConfigContext);
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const getBusinessEntities = () => {
    const storedRole = StorageService.getItem(ROLES);
    const roles = storedRole ? (JSON.parse(storedRole) as { roleId?: string }) : undefined;
    // use optional chaining to call API only if roleId exists
    return roles?.roleId ? getBusinessEntitiesDetail(roles.roleId) : undefined;
  };

  // Initialize MSAL redirect handling and login
  const initAuth = async () => {
    // Prevent multiple simultaneous authentication attempts
    if (isAuthInProgress) {
      return;
    }

    try {
      setIsAuthInProgress(true);
      
      // Check if there's already an active account
      if (msalInstance.getActiveAccount()) {
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) {
          const tokenResponse = await acquireAccessToken(activeAccount.username);
          const graphApiResponse = await acquireGraphToken(activeAccount.username);
          await processToken(tokenResponse, graphApiResponse);
          setShowMapPage(true);
          return;
        }
      }
      
      // Handle redirect response if any and set active account
      const response = await msalInstance.handleRedirectPromise();
      
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
        
        // For redirect response, we need to acquire tokens properly
        const tokenResponse = await acquireAccessToken(response.account.username);
        const graphApiResponse = await acquireGraphToken(response.account.username);
        await processToken(tokenResponse, graphApiResponse);
        logAnalyticsEvent('web_user_successfully_logged_in', { description: 'User successfully logged in' });
        setShowMapPage(true);
        return;
      }
      
      const activeAccount = getActiveAccount();
      
      if (!activeAccount) {
        // Add a small delay to prevent rapid redirects
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // No logged-in user, initiate login via redirect
        const scope = process.env.REACT_APP_MSAL_SCOPE ?? '';
        
        await msalInstance.loginRedirect({ 
          scopes: [scope],
          prompt: 'select_account'
        });
        return;
      }
      
      // User is active, acquire tokens and process
      const tokenResponse = await acquireAccessToken(activeAccount.username);
      const graphApiResponse = await acquireGraphToken(activeAccount.username);
      
      await processToken(tokenResponse, graphApiResponse);
      setShowMapPage(true);
    } catch (error) {
      const errorDetails: any = error;
      if (errorDetails?.errorCode === 'interaction_in_progress') {
        setIsAuthInProgress(false);
        return;
      }
      setIsAuthInProgress(false);
    }finally {
          setTimeout(() => setIsAuthInProgress(false), 1000);
        }
      };

  // Helper to add timeout to a promise
  function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('timeout'));
      }, ms);
      promise.then((value) => {
        clearTimeout(timer);
        resolve(value);
      }, (err) => {
        clearTimeout(timer);
        reject(err);
      });
    });
  }

  const processToken = async (accessToken: any, graphToken: any) => {
    StorageService.setItem(ACCESS_TKN_KEY, accessToken.accessToken);
    StorageService.setItem(GRAPH_TOKEN, graphToken.accessToken);

    appConfigStore.setUserData(accessToken.account);

    // process roles
    if (accessToken.idTokenClaims) {
      const { roles } = accessToken.idTokenClaims;
      const roleData: UserRoleType | null = getRoleByUser(roles);
      StorageService.setItem(ROLES, JSON.stringify(roleData));
    }

    const userData = getUserData(accessToken.account);
    StorageService.setItem(USER_DATA, JSON.stringify(userData));
    
    // Re-initialize Firebase after userData is set, to ensure analytics disabling works for devs
    firebase();

    try {
      // Add a timeout (10 seconds) to the business entities API call
      const businessEntitiesPromise = getBusinessEntities();
      if (!businessEntitiesPromise) {
        setAuthError(t('noBusinessEntitiesFound'));
        setIsAuthInProgress(false);
        return;
      }
      const businessEntities = await withTimeout(businessEntitiesPromise, 10000);
      if (businessEntities) {
        const businessEntityId = businessEntities[0].businessEntityObjectId;
        StorageService.setItem(BUSINESS_ENTITY, businessEntityId);
        try {
          // Add a timeout (10 seconds) to the login API call
          await withTimeout(login(businessEntityId), 10000);
          setShowMapPage(true);
        } catch (loginErr: any) {
          if (loginErr?.message === 'timeout') {
            setAuthError(t('Login request timed out. Please check your connection or try again.'));
          } else {
            setAuthError(t('Login failed. Please try again.'));
          }
          setIsAuthInProgress(false);
        }
      } else {
        setAuthError(t('noBusinessEntitiesFound'));
        setIsAuthInProgress(false);
      }
    } catch (err: any) {
      if (err?.message === 'timeout') {
        setAuthError(t('Request timed out. Please check your connection or try again.'));
      } else {
        setAuthError(t('Failed to load business entities. Please try again.'));
      }
      setIsAuthInProgress(false);
    }
  }

  const getUserData = (user: any) => {
    return {
      name: user.name ?? user.username,
      email: user.username,
    }
  }

  const getRoleByUser = (
    roles: string[]
  ): { role: string; roleId: string } | null => {
    let assignedRole: UserRoleType | null = null;
    const rolesDummy = [
      {
        role: Roles.BUSINESS_ENTITY_ADMIN,
        roleId: process.env.REACT_APP_BUSINESS_ENTITY_ADMINISTRATOR_ROLE_ID ?? '',
      },
      {
        role: Roles.APP_ADMIN,
        roleId: process.env.REACT_APP_APPLICATION_ADMINISTRATOR_ROLE_ID ?? '',
      },
      {
        role: Roles.APP_USER,
        roleId: process.env.REACT_APP_APPLICATION_USER_ROLE_ID ?? '',
      },
      {
        role: Roles.GUEST,
        roleId: process.env.REACT_APP_GUEST_ROLE_ID ?? '',
      },
    ];
    if (roles.length === 0) {
      assignedRole = null;
    } else {
      const r = roles[roles.length - 1];
      rolesDummy.forEach((role) => {
        if (r == role.role) {
          assignedRole = role;
        }
      })
    }
    return assignedRole;
  };


  // Retry handler
  const handleRetry = () => {
    setAuthError(null);
    setIsAuthInProgress(false);
    setTimeout(() => {
      initAuth();
    }, 100);
  };

  // Logout handler
  const handleLogout = () => {
    StorageService.clear();
    // window.location.reload(); // Remove this
    msalInstance.logoutRedirect();
  }
  useEffect(() => {
    const msalInteractionStatus = window.localStorage.getItem('msal.interaction.status');
    // If stuck for too long, clear and force logout
    if (msalInteractionStatus === 'interaction_in_progress') {
      // Optionally, check how long it's been stuck and only clear if > 1 min
      window.localStorage.removeItem('msal.interaction.status');
      msalInstance.logoutRedirect();
      return;
    }
    if (!isAuthInProgress && !authError) {
      initAuth();
    }
  }, [isAuthInProgress, authError]);

  return (
    <div className="authentication-page">
      {authError ? (
        <div className="login-spinner">
          <p style={{ color: 'red', marginBottom: 16 }}>{authError}</p>
          <button onClick={handleRetry} style={{ marginRight: 12 }}>{t('Retry')}</button>
          <button onClick={handleLogout}>{t('Logout')}</button>
        </div>
      ) : (
        <LoadingSpinner darkMode={darkMode} message={t("authenticating")} />
      )}
    </div>
  );
}

export default AuthenticationPage;